package br.com.vvrefeicoes.portal.feature.rh

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.components.AppPagination
import br.com.vvrefeicoes.portal.core.ui.components.EmptyState
import br.com.vvrefeicoes.portal.core.ui.layout.AppHorizontalScrollbar
import br.com.vvrefeicoes.portal.core.ui.theme.AppPalette
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.core.util.MonthCalendar
import br.com.vvrefeicoes.portal.domain.model.RhNetPayrollStatus
import br.com.vvrefeicoes.portal.domain.model.RhRanking
import br.com.vvrefeicoes.portal.domain.model.RhSummary
import br.com.vvrefeicoes.portal.domain.model.RhSummaryMonth
import br.com.vvrefeicoes.portal.domain.model.RhSummaryUnit
import br.com.vvrefeicoes.portal.feature.operacional.ChartCard
import com.composables.icons.lucide.ArrowRightLeft
import com.composables.icons.lucide.Banknote
import com.composables.icons.lucide.CircleAlert
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.Scale
import com.composables.icons.lucide.TrendingUp
import com.composables.icons.lucide.UserPlus
import com.composables.icons.lucide.Users
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.rh_chart_absenteeism
import portalvv.shared.generated.resources.rh_chart_active
import portalvv.shared.generated.resources.rh_chart_movements
import portalvv.shared.generated.resources.rh_chart_net_overtime
import portalvv.shared.generated.resources.rh_chart_turnover
import portalvv.shared.generated.resources.rh_chart_variables
import portalvv.shared.generated.resources.rh_col_advance
import portalvv.shared.generated.resources.rh_col_cost_allowance
import portalvv.shared.generated.resources.rh_col_employees
import portalvv.shared.generated.resources.rh_col_hazardous
import portalvv.shared.generated.resources.rh_col_insalubrity
import portalvv.shared.generated.resources.rh_col_name
import portalvv.shared.generated.resources.rh_col_night_shift
import portalvv.shared.generated.resources.rh_col_payment
import portalvv.shared.generated.resources.rh_col_registrations
import portalvv.shared.generated.resources.rh_col_salary_difference
import portalvv.shared.generated.resources.rh_col_total
import portalvv.shared.generated.resources.rh_col_unit
import portalvv.shared.generated.resources.rh_kpi_absenteeism
import portalvv.shared.generated.resources.rh_kpi_active
import portalvv.shared.generated.resources.rh_kpi_active_variation
import portalvv.shared.generated.resources.rh_kpi_admitted
import portalvv.shared.generated.resources.rh_kpi_dismissed
import portalvv.shared.generated.resources.rh_kpi_net_payroll
import portalvv.shared.generated.resources.rh_kpi_overtime
import portalvv.shared.generated.resources.rh_kpi_turnover
import portalvv.shared.generated.resources.rh_kpi_variable_costs
import portalvv.shared.generated.resources.rh_legend_bonus
import portalvv.shared.generated.resources.rh_legend_productivity
import portalvv.shared.generated.resources.rh_net_status_awaiting_advance
import portalvv.shared.generated.resources.rh_net_status_awaiting_payment
import portalvv.shared.generated.resources.rh_panel_empty
import portalvv.shared.generated.resources.rh_panel_not_imported
import portalvv.shared.generated.resources.rh_ranking_director
import portalvv.shared.generated.resources.rh_ranking_gestora
import portalvv.shared.generated.resources.rh_ranking_manager
import portalvv.shared.generated.resources.rh_units_title
import kotlin.math.roundToInt

private val KPI_MIN_WIDTH = 240.dp
private val CHART_MIN_WIDTH = 430.dp
private val RANKING_MIN_WIDTH = 360.dp
private val UNIT_NAME_WIDTH = 240.dp
private val UNIT_VALUE_WIDTH = 124.dp
private val RANKING_VALUE_WIDTH = 120.dp
private val RANKING_COUNT_WIDTH = 88.dp
private const val UNITS_PAGE_SIZE = 15
private const val RANKING_LIMIT = 10

private val NET_PAYROLL_COLOR = AppPalette.Primary500
private val OVERTIME_COLOR = AppPalette.Warning500
private val BONUS_COLOR = AppPalette.Accent500
private val PRODUCTIVITY_COLOR = AppPalette.Info500
private val ADMITTED_COLOR = AppPalette.Success500
private val DISMISSED_COLOR = AppPalette.Error500
private val TURNOVER_COLOR = AppPalette.Accent900
private val ABSENTEEISM_COLOR = AppPalette.Error500
private val ACTIVE_COLOR = AppPalette.Primary500

@Composable
fun RhSummaryContent(summary: RhSummary) {
    val current = summary.current
    if (current == null) {
        EmptyState(message = stringResource(Res.string.rh_panel_empty))
        return
    }
    RhSummaryKpis(current = current, activeVariationPercent = summary.activeVariationPercent)
    RhEvolutionCharts(monthly = summary.monthly)
    RhUnitsTable(units = summary.units, costsImported = current.costsImported)
    RhGrid(
        minColumnWidth = RANKING_MIN_WIDTH,
        maxColumns = 3,
        items =
            listOf(
                { modifier -> RhRankingCard(stringResource(Res.string.rh_ranking_director), summary.netPayrollByDirector, modifier) },
                { modifier -> RhRankingCard(stringResource(Res.string.rh_ranking_gestora), summary.netPayrollByGestora, modifier) },
                { modifier -> RhRankingCard(stringResource(Res.string.rh_ranking_manager), summary.netPayrollByManager, modifier) },
            ),
    )
}

@Composable
private fun RhSummaryKpis(
    current: RhSummaryMonth,
    activeVariationPercent: Double?,
) {
    val notImported = stringResource(Res.string.rh_panel_not_imported)
    val variationHint =
        activeVariationPercent?.let { variation ->
            val signed = if (variation > 0) "+${rhDecimal(variation, 1)}%" else "${rhDecimal(variation, 1)}%"
            stringResource(Res.string.rh_kpi_active_variation, signed)
        }
    val netPayrollHint =
        when (current.netPayrollStatus) {
            RhNetPayrollStatus.AWAITING_ADIANTAMENTO -> stringResource(Res.string.rh_net_status_awaiting_advance)
            RhNetPayrollStatus.AWAITING_PAGAMENTO -> stringResource(Res.string.rh_net_status_awaiting_payment)
            RhNetPayrollStatus.COMPLETE, RhNetPayrollStatus.MISSING -> null
        }
    RhGrid(
        minColumnWidth = KPI_MIN_WIDTH,
        maxColumns = 4,
        items =
            listOf(
                { modifier ->
                    RhKpiTile(
                        icon = Lucide.Users,
                        iconColor = AppPalette.Primary500,
                        iconContainerColor = AppPalette.Primary100,
                        label = stringResource(Res.string.rh_kpi_active),
                        value = if (current.costsImported) current.active.toString() else notImported,
                        hint = variationHint,
                        modifier = modifier,
                    )
                },
                { modifier ->
                    RhKpiTile(
                        icon = Lucide.UserPlus,
                        iconColor = AppPalette.Success500,
                        iconContainerColor = AppPalette.Success100,
                        label = "${stringResource(Res.string.rh_kpi_admitted)} / ${stringResource(Res.string.rh_kpi_dismissed)}",
                        value = if (current.costsImported) "${current.admitted} / ${current.dismissed}" else notImported,
                        modifier = modifier,
                    )
                },
                { modifier ->
                    RhKpiTile(
                        icon = Lucide.ArrowRightLeft,
                        iconColor = AppPalette.Accent900,
                        iconContainerColor = AppPalette.Accent100,
                        label = stringResource(Res.string.rh_kpi_turnover),
                        value = if (current.costsImported) rhPercent(current.turnoverPercent) else notImported,
                        modifier = modifier,
                    )
                },
                { modifier ->
                    RhKpiTile(
                        icon = Lucide.CircleAlert,
                        iconColor = AppPalette.Error500,
                        iconContainerColor = AppPalette.Error100,
                        label = stringResource(Res.string.rh_kpi_absenteeism),
                        value = current.absenteeismPercent?.let(::rhPercent) ?: notImported,
                        modifier = modifier,
                    )
                },
                { modifier ->
                    RhKpiTile(
                        icon = Lucide.TrendingUp,
                        iconColor = AppPalette.Warning500,
                        iconContainerColor = AppPalette.Warning100,
                        label = stringResource(Res.string.rh_kpi_overtime),
                        value = if (current.costsImported) rhMoney(current.overtime) else notImported,
                        modifier = modifier,
                    )
                },
                { modifier ->
                    RhKpiTile(
                        icon = Lucide.Scale,
                        iconColor = AppPalette.Info900,
                        iconContainerColor = AppPalette.Info100,
                        label = stringResource(Res.string.rh_kpi_variable_costs),
                        value = if (current.costsImported) rhMoney(current.totalCost) else notImported,
                        modifier = modifier,
                    )
                },
                { modifier ->
                    RhKpiTile(
                        icon = Lucide.Banknote,
                        iconColor = AppPalette.Primary500,
                        iconContainerColor = AppPalette.Primary100,
                        label = stringResource(Res.string.rh_kpi_net_payroll),
                        value = current.netPayroll?.let(::rhMoney) ?: notImported,
                        hint = netPayrollHint,
                        modifier = modifier,
                    )
                },
            ),
    )
}

@Composable
private fun RhEvolutionCharts(monthly: List<RhSummaryMonth>) {
    val labels = monthly.map { MonthCalendar.shortLabel(it.period) }
    val moneyLabel: (Float) -> String = { value -> rhMoneyCompact(value.toDouble()) }
    val countLabel: (Float) -> String = { value -> value.roundToInt().toString() }
    val percentLabel: (Float) -> String = { value -> rhPercent(value.toDouble()) }
    RhGrid(
        minColumnWidth = CHART_MIN_WIDTH,
        maxColumns = 2,
        items =
            listOf(
                { modifier ->
                    RhGroupedBarCard(
                        title = stringResource(Res.string.rh_chart_net_overtime),
                        labels = labels,
                        series =
                            listOf(
                                RhChartSeries(
                                    label = stringResource(Res.string.rh_kpi_net_payroll),
                                    color = NET_PAYROLL_COLOR,
                                    values = monthly.map { (it.netPayroll ?: 0.0).toFloat() },
                                ),
                                RhChartSeries(
                                    label = stringResource(Res.string.rh_kpi_overtime),
                                    color = OVERTIME_COLOR,
                                    values = monthly.map { it.overtime.toFloat() },
                                ),
                            ),
                        format = moneyLabel,
                        modifier = modifier,
                    )
                },
                { modifier ->
                    RhGroupedBarCard(
                        title = stringResource(Res.string.rh_chart_variables),
                        labels = labels,
                        series =
                            listOf(
                                RhChartSeries(
                                    label = stringResource(Res.string.rh_legend_bonus),
                                    color = BONUS_COLOR,
                                    values = monthly.map { it.bonus.toFloat() },
                                ),
                                RhChartSeries(
                                    label = stringResource(Res.string.rh_legend_productivity),
                                    color = PRODUCTIVITY_COLOR,
                                    values = monthly.map { it.productivity.toFloat() },
                                ),
                            ),
                        format = moneyLabel,
                        modifier = modifier,
                    )
                },
                { modifier ->
                    RhGroupedBarCard(
                        title = stringResource(Res.string.rh_chart_movements),
                        labels = labels,
                        series =
                            listOf(
                                RhChartSeries(
                                    label = stringResource(Res.string.rh_kpi_admitted),
                                    color = ADMITTED_COLOR,
                                    values = monthly.map { it.admitted.toFloat() },
                                ),
                                RhChartSeries(
                                    label = stringResource(Res.string.rh_kpi_dismissed),
                                    color = DISMISSED_COLOR,
                                    values = monthly.map { it.dismissed.toFloat() },
                                ),
                            ),
                        format = countLabel,
                        modifier = modifier,
                    )
                },
                { modifier ->
                    RhLineCard(
                        title = stringResource(Res.string.rh_chart_turnover),
                        legend = stringResource(Res.string.rh_kpi_turnover),
                        labels = labels,
                        values = monthly.map { it.turnoverPercent.toFloat() },
                        color = TURNOVER_COLOR,
                        format = percentLabel,
                        modifier = modifier,
                    )
                },
                { modifier ->
                    RhLineCard(
                        title = stringResource(Res.string.rh_chart_absenteeism),
                        legend = stringResource(Res.string.rh_kpi_absenteeism),
                        labels = labels,
                        values = monthly.map { it.absenteeismPercent?.toFloat() },
                        color = ABSENTEEISM_COLOR,
                        format = percentLabel,
                        modifier = modifier,
                    )
                },
                { modifier ->
                    RhLineCard(
                        title = stringResource(Res.string.rh_chart_active),
                        legend = stringResource(Res.string.rh_kpi_active),
                        labels = labels,
                        values = monthly.map { it.active.toFloat() },
                        color = ACTIVE_COLOR,
                        format = countLabel,
                        modifier = modifier,
                    )
                },
            ),
    )
}

private class RhUnitColumn(
    val title: String,
    val value: (RhSummaryUnit) -> String,
)

@Composable
private fun RhUnitsTable(
    units: List<RhSummaryUnit>,
    costsImported: Boolean,
) {
    val notImported = RH_EMPTY_VALUE
    val employeesTitle = stringResource(Res.string.rh_col_employees)
    val absenteeismTitle = stringResource(Res.string.rh_kpi_absenteeism)
    val columns =
        listOf(
            RhUnitColumn(stringResource(Res.string.rh_col_employees)) { it.employees.toString() },
            RhUnitColumn(stringResource(Res.string.rh_kpi_admitted)) { it.admitted.toString() },
            RhUnitColumn(stringResource(Res.string.rh_kpi_dismissed)) { it.dismissed.toString() },
            RhUnitColumn(stringResource(Res.string.rh_kpi_turnover)) { rhPercent(it.turnoverPercent) },
            RhUnitColumn(stringResource(Res.string.rh_kpi_absenteeism)) { it.absenteeismPercent?.let(::rhPercent) ?: notImported },
            RhUnitColumn(stringResource(Res.string.rh_kpi_overtime)) { rhMoney(it.overtime) },
            RhUnitColumn(stringResource(Res.string.rh_col_night_shift)) { rhMoney(it.nightShift) },
            RhUnitColumn(stringResource(Res.string.rh_legend_bonus)) { rhMoney(it.bonus) },
            RhUnitColumn(stringResource(Res.string.rh_legend_productivity)) { rhMoney(it.productivity) },
            RhUnitColumn(stringResource(Res.string.rh_col_cost_allowance)) { rhMoney(it.costAllowance) },
            RhUnitColumn(stringResource(Res.string.rh_col_salary_difference)) { rhMoney(it.salaryDifference) },
            RhUnitColumn(stringResource(Res.string.rh_col_insalubrity)) { rhMoney(it.insalubrity) },
            RhUnitColumn(stringResource(Res.string.rh_col_hazardous)) { rhMoney(it.hazardousDuty) },
            RhUnitColumn(stringResource(Res.string.rh_kpi_variable_costs)) { rhMoney(it.totalCost) },
            RhUnitColumn(stringResource(Res.string.rh_col_advance)) { rhMoney(it.advance) },
            RhUnitColumn(stringResource(Res.string.rh_col_payment)) { rhMoney(it.payment) },
            RhUnitColumn(stringResource(Res.string.rh_kpi_net_payroll)) { rhMoney(it.netPayroll) },
        ).filter { column ->
            // Sem a planilha de custos só há o que vem do absenteísmo: as demais colunas seriam zeros.
            costsImported || column.title == employeesTitle || column.title == absenteeismTitle
        }
    var page by remember(units) { mutableStateOf(0) }
    val pageCount = (units.size + UNITS_PAGE_SIZE - 1) / UNITS_PAGE_SIZE
    val visible = units.drop(page * UNITS_PAGE_SIZE).take(UNITS_PAGE_SIZE)
    val scrollState = rememberScrollState()

    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(AppSpacing.space16),
            verticalArrangement = Arrangement.spacedBy(AppSpacing.space12),
        ) {
            Text(
                text = stringResource(Res.string.rh_units_title),
                style = MaterialTheme.typography.titleMedium,
            )
            Box(modifier = Modifier.fillMaxWidth().horizontalScroll(scrollState)) {
                Column {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        RhTableCell(
                            text = stringResource(Res.string.rh_col_unit),
                            width = UNIT_NAME_WIDTH,
                            header = true,
                            align = TextAlign.Start,
                        )
                        columns.forEach { column ->
                            RhTableCell(text = column.title, width = UNIT_VALUE_WIDTH, header = true)
                        }
                    }
                    HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                    visible.forEach { unit ->
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            RhTableCell(text = unit.name, width = UNIT_NAME_WIDTH, align = TextAlign.Start)
                            columns.forEach { column ->
                                RhTableCell(text = column.value(unit), width = UNIT_VALUE_WIDTH)
                            }
                        }
                        HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                    }
                }
            }
            AppHorizontalScrollbar(
                state = scrollState,
                modifier = Modifier.fillMaxWidth(),
            )
            AppPagination(
                page = page,
                pageCount = pageCount,
                onPageChange = { page = it.coerceIn(0, (pageCount - 1).coerceAtLeast(0)) },
                modifier = Modifier.align(Alignment.End),
            )
        }
    }
}

@Composable
internal fun RhTableCell(
    text: String,
    width: Dp,
    header: Boolean = false,
    align: TextAlign = TextAlign.End,
) {
    Text(
        text = text,
        style = if (header) MaterialTheme.typography.labelMedium else MaterialTheme.typography.bodySmall,
        color = if (header) MaterialTheme.colorScheme.onSurfaceVariant else MaterialTheme.colorScheme.onSurface,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis,
        textAlign = align,
        modifier = Modifier.width(width).padding(horizontal = AppSpacing.space8, vertical = AppSpacing.space8),
    )
}

/** Folha líquida agrupada (gerente, diretoria ou gestora): os maiores valores e o total geral. */
@Composable
internal fun RhRankingCard(
    title: String,
    ranking: List<RhRanking>,
    modifier: Modifier = Modifier,
) {
    ChartCard(title = title, modifier = modifier) {
        if (ranking.isEmpty()) {
            RhEmptyChart()
        } else {
            RhRankingRow(
                name = stringResource(Res.string.rh_col_name),
                employees = stringResource(Res.string.rh_col_registrations),
                total = stringResource(Res.string.rh_col_total),
                header = true,
            )
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
            ranking.take(RANKING_LIMIT).forEach { item ->
                RhRankingRow(name = item.name, employees = item.employees.toString(), total = rhMoney(item.total))
            }
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
            RhRankingRow(
                name = stringResource(Res.string.rh_col_total),
                employees = ranking.sumOf { it.employees }.toString(),
                total = rhMoney(ranking.sumOf { it.total }),
                emphasized = true,
            )
        }
    }
}

@Composable
private fun RhRankingRow(
    name: String,
    employees: String,
    total: String,
    header: Boolean = false,
    emphasized: Boolean = false,
) {
    val style = if (header) MaterialTheme.typography.labelMedium else MaterialTheme.typography.bodyMedium
    val color = if (header) MaterialTheme.colorScheme.onSurfaceVariant else MaterialTheme.colorScheme.onSurface
    val weight = if (emphasized) FontWeight.SemiBold else null
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            text = name,
            style = style,
            color = color,
            fontWeight = weight,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
            modifier = Modifier.weight(1f),
        )
        Text(
            text = employees,
            style = style,
            color = color,
            fontWeight = weight,
            maxLines = 1,
            textAlign = TextAlign.End,
            modifier = Modifier.width(RANKING_COUNT_WIDTH),
        )
        Text(
            text = total,
            style = style,
            color = color,
            fontWeight = weight,
            maxLines = 1,
            textAlign = TextAlign.End,
            modifier = Modifier.width(RANKING_VALUE_WIDTH),
        )
    }
}
